// SSE-over-POST parser + iterators for the streaming forecast endpoint.
//
// EventSource is GET-only and we need POST (the question body is too large
// for a URL). ~30 lines of vanilla code; standard pattern.
//
// parseSSEStream(body, signal?) — yields { event: string, data: any } for
// every complete frame. Unknown event types pass through (forward-compat —
// server may add new events without coordinated client deploys). Malformed
// JSON in a non-terminal data line drops just that frame; a malformed `done`
// is surfaced with null data so consumers cannot mistake it for clean EOF.

import { intentEmitter, intentHeaders, isIntentId } from "./telemetry/intentEmitter.js";
import { clientReqIdOf, createStreamTrace, traceStream } from "./telemetry/streamTrace.js";

/**
 * The run-intent hooks of one forecast stream (P2 design §4.6): the stream carries the
 * intent of the request that opened it (`opts.intentId`) or, for a resume, the intent bound to
 * its trace; its first frame marks `first_frame_ms` and its terminal frame ends the intent.
 */
function runIntentHooks(intentId) {
  let traceId = null;
  let firstFrame = false;
  return {
    setTrace(id) {
      traceId = id || null;
    },
    frame(type) {
      if (firstFrame || !traceId || type === FRAME.HEARTBEAT) return;
      firstFrame = true;
      try {
        intentEmitter().markRun(traceId, "first_frame_ms");
      } catch {
        /* telemetry never breaks the stream */
      }
    },
    onTerminal(outcome) {
      if (traceId && intentId) intentEmitter().endRun({ traceId }, outcome);
    },
  };
}

/**
 * SSE frame vocabulary reference. The server (server/proxy/forecast.js) handles
 * the subset {progress, update, heartbeat, done, error} — `update` (the typed
 * sooth_idl.progress frame) is kept, not interpreted, so the resume path can
 * replay it; cancelled/catchup are pass-through; traceAllocated is synthesized client-side from the
 * x-forecaster-trace-id response header. NOT wired into runtime code: the SSE
 * consumers match raw string literals rather than importing FRAME (only tests
 * reference it), so this documents the vocabulary but doesn't enforce it.
 */
export const FRAME = Object.freeze({
  PROGRESS: "progress",
  UPDATE: "update",
  NARRATION: "narration",
  HEARTBEAT: "heartbeat",
  DONE: "done",
  ERROR: "error",
  CANCELLED: "cancelled",
  CATCHUP: "catchup",
  TRACE_ALLOCATED: "traceAllocated",
});

/**
 * `onFrame(type, text)`, when given, is told each complete frame's event type and its
 * normalized text, without the blank-line delimiter (the telemetry stream tracer,
 * lib/telemetry/streamTrace.js, sizes it only while tracing).
 */
export async function* parseSSEStream(body, signal, onFrame) {
  const reader = body.getReader();
  const onAbort = () => {
    void reader.cancel().catch(() => {}); // swallow-ok: cleanup
  };
  signal?.addEventListener("abort", onAbort, { once: true });
  const dec = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      if (signal && signal.aborted) return;
      const { value, done } = await reader.read();
      if (done) break;
      // Normalize CRLF → LF so a CRLF-emitting proxy (allowed by the SSE
      // spec) doesn't stall the splitter on \n\n.
      buffer += dec.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        let event = null;
        let dataLine = null;
        for (const line of frame.split("\n")) {
          if (line.startsWith("event: ")) event = line.slice(7);
          else if (line.startsWith("data: ")) dataLine = line.slice(6);
        }
        if (event === null || dataLine === null) continue;
        if (onFrame) {
          try {
            onFrame(event, frame);
          } catch {
            /* telemetry never breaks the stream */
          }
        }
        let data;
        try {
          data = JSON.parse(dataLine);
        } catch {
          if (event !== FRAME.DONE) continue; // skip malformed non-terminal frame
          data = null;
        }
        yield { event, data };
      }
    }
  } finally {
    signal?.removeEventListener("abort", onAbort);
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

// POST /api/forecasts/run, returning an async iterator over typed events.
//
// When the response carries an `x-forecaster-trace-id` header, the first
// yielded event is `{ type: 'traceAllocated', traceId, forecastId, backend, requestId }`
// (synthesized from response headers — chatboard's Phase B proxy emits
// `x-forecaster-trace-id`, `x-chatboard-forecast-id`, and `x-forecast-backend`).
// The forecast id is the forecasts.id UUID used by the messages-persistence API;
// the trace id is the streaming lifecycle key used by cancel/result/resume;
// `backend` here is the `x-forecast-backend` DEPLOY LABEL (a server constant),
// NOT the run engine/orchestrator — genForecastFromServer intentionally ignores
// this field and derives run.backend/_obs.backend from the requested engine
// instead (see forecastClient.js). Do not stamp run.backend from it. May be null
// if the header is absent. `requestId` is the x-request-id correlation UUID.
// When the trace-id header is absent, no `traceAllocated` is synthesized (a null
// trace id would later build a broken URL like `/api/forecasts/null/result`).
//
// Either way, the underlying SSE events then flow through, retyped from
// {event, data} to {type: event, data}.
export async function* runForecastStream(body, signal, opts = {}) {
  if (!Number.isFinite(opts.recoveryDeadlineAt)) {
    yield* streamForecastRun(body, signal, opts);
    return;
  }
  if (signal?.aborted) throw cancelledError();
  const now = opts.now || Date.now;
  if (now() >= opts.recoveryDeadlineAt) throw recoveryWaitExpiredError();
  const controller = new AbortController();
  let interrupt;
  const interrupted = new Promise((_, reject) => {
    interrupt = reject;
  });
  const onAbort = () => {
    interrupt(cancelledError());
    controller.abort();
  };
  signal?.addEventListener("abort", onAbort, { once: true });
  const timer = setTimeout(() => {
    interrupt(recoveryWaitExpiredError());
    controller.abort();
  }, opts.recoveryDeadlineAt - now());
  const stream = streamForecastRun(body, controller.signal, opts);
  try {
    while (true) {
      const next = await Promise.race([stream.next(), interrupted]);
      if (next.done) return;
      yield next.value;
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
    controller.abort();
    void stream.return().catch(() => {}); // swallow-ok: cleanup
  }
}

function streamForecastRun(body, signal, opts) {
  const intentId = isIntentId(opts.intentId) ? opts.intentId : null;
  const trace = createStreamTrace(
    intentId ? { stream: "forecast", intent_id: intentId } : { stream: "forecast" },
  );
  const hooks = runIntentHooks(intentId);
  return traceStream(
    trace,
    signal,
    streamForecastRunFrames(body, signal, opts, trace, intentId, hooks),
    { onTerminal: hooks.onTerminal },
  );
}

async function* streamForecastRunFrames(body, signal, opts, trace, intentId = null, hooks = null) {
  const fetchImpl = opts.fetchImpl || fetch;
  const resp = await fetchImpl("/api/forecasts/run", {
    method: "POST",
    headers: { "content-type": "application/json", ...intentHeaders(intentId) },
    credentials: "include",
    body: JSON.stringify(body),
    signal,
  });
  trace.start({
    route: "/api/forecasts/run",
    request_id: resp.headers?.get?.("x-request-id") ?? null,
    client_req_id: clientReqIdOf(resp),
    trace_id: resp.headers?.get?.("x-forecaster-trace-id") ?? null,
    forecast_id: resp.headers?.get?.("x-chatboard-forecast-id") ?? null,
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const traceId = resp.headers.get("x-forecaster-trace-id");
  const forecastId = resp.headers.get("x-chatboard-forecast-id");
  // The proxy sets the x-forecast-backend deploy label on the stream-start response.
  // We still surface it on the frame, but genForecastFromServer now IGNORES it and
  // derives run.backend/_obs.backend from the requested engine instead (the label is
  // a server-side constant; letting it become run.backend broke branch-create). The
  // frame field is retained pending the later server-side x-forecast-backend
  // consolidation. May be null if the header is absent; callers tolerate that.
  const backend = resp.headers.get("x-forecast-backend");
  // x-request-id: the per-request correlation UUID the proxy echoes on the
  // stream-start response (server.js requestContext → forecast.js). Carried so
  // _obs.requestId is populated on the streaming path too (the observability
  // panel's "last request"); previously only the non-streaming fallback read it.
  const requestId = resp.headers.get("x-request-id");
  // traceId is the resume/cancel/result key, and the sessionStorage handle
  // is useless without it. Only synthesize traceAllocated when traceId is
  // present — emitting with traceId=null would later build a broken URL
  // like `/api/forecasts/null/result`.
  if (traceId && intentId) {
    // From here any tab that sees this run end can end its intent (the map is shared).
    try {
      intentEmitter().bindTrace(intentId, traceId, {
        runId: typeof body?.client_id === "string" ? body.client_id : null,
      });
    } catch {
      /* telemetry never breaks the stream */
    }
    hooks?.setTrace(traceId);
  }
  if (traceId) {
    yield { type: FRAME.TRACE_ALLOCATED, traceId, forecastId, backend, requestId };
  }
  for await (const { event, data } of parseSSEStream(resp.body, signal, (type, text) => {
    trace.frameOf(type, text, 2);
    hooks?.frame(type);
  })) {
    yield { type: event, data };
  }
}

// A typed update's position in its run's log (sooth_idl.progress `seq`: a
// non-negative safe integer), or null for a frame with no usable one. The resume
// cursor is the highest such seq this tab has seen.
export function typedSeqOf(value) {
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

// GET /api/forecasts/run/:trace_id — resume an in-progress forecast.
// First event yielded by the upstream is `catchup` carrying current row
// state; then either polled progress or a terminal event. With `opts.sinceSeq`
// (the highest typed `update` seq already folded), the server replays the typed
// frames strictly after it right behind `catchup`, then relays live ones — the
// resume path for the typed channel after a Cloud Run 1200 s cut (#5611).
export function resumeForecastStream(traceId, signal, opts = {}) {
  // The run's own intent (bound when its stream opened, possibly in another tab or before a
  // reload); else the intent that resumed it.
  let intentId = null;
  try {
    intentId = intentEmitter().intentIdForTrace(traceId);
  } catch {
    intentId = null;
  }
  const runIntent = Boolean(intentId);
  if (!intentId && isIntentId(opts.intentId)) intentId = opts.intentId;
  const trace = createStreamTrace(
    intentId
      ? { stream: "resume", trace_id: traceId, intent_id: intentId }
      : { stream: "resume", trace_id: traceId },
  );
  const hooks = runIntentHooks(runIntent ? intentId : null);
  if (runIntent) hooks.setTrace(traceId);
  return traceStream(
    trace,
    signal,
    resumeForecastFrames(traceId, signal, opts, trace, intentId, hooks),
    {
      onTerminal: hooks.onTerminal,
    },
  );
}

async function* resumeForecastFrames(traceId, signal, opts, trace, intentId = null, hooks = null) {
  const fetchImpl = opts.fetchImpl || fetch;
  const sinceSeq = typedSeqOf(opts.sinceSeq);
  const query = sinceSeq === null ? "" : `?since_seq=${sinceSeq}`;
  const resp = await fetchImpl(`/api/forecasts/run/${encodeURIComponent(traceId)}${query}`, {
    method: "GET",
    credentials: "include",
    signal,
    ...(intentId ? { headers: intentHeaders(intentId) } : {}),
  });
  trace.start({
    route: "/api/forecasts/run/:id",
    request_id: resp.headers?.get?.("x-request-id") ?? null,
    client_req_id: clientReqIdOf(resp),
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  for await (const { event, data } of parseSSEStream(resp.body, signal, (type, text) => {
    trace.frameOf(type, text, 2);
    if (type !== "catchup") hooks?.frame(type);
  })) {
    yield { type: event, data };
  }
}

// One absolute browser wait budget, never renewed by reconnects or reloads.
export const FORECAST_RECOVERY_WINDOW_MS = 262 * 60 * 1000;

const RECOVERY_WAIT_EXPIRED = "forecast_recovery_wait_expired";
const RESULT_INSPECTION_TIMED_OUT = "result_inspection_timed_out";
const INVALID_RESUME_DONE = "invalid_resume_done";

const recoveryWaitExpiredError = () =>
  Object.assign(new Error("Forecast recovery wait expired"), {
    code: RECOVERY_WAIT_EXPIRED,
  });

// Coded so callers can tell "the READ stalled" from "the result is not there". The two
// want opposite handling: a stall is transient and worth another look, a 404 is an answer.
const resultInspectionTimedOutError = () =>
  Object.assign(new Error("Forecast result inspection timed out"), {
    code: RESULT_INSPECTION_TIMED_OUT,
  });

const cancelledError = () => Object.assign(new Error("cancelled"), { name: "AbortError" });

// The message vocabulary of a failed fetch — no response at all, or the body reader dying
// mid-stream: Chromium "Failed to fetch" / "network error", WebKit "Load failed", Gecko
// "NetworkError when attempting to fetch resource.", undici (Node) "fetch failed". A
// MESSAGE match on purpose, never `instanceof TypeError`: fetch reports every network
// failure as a TypeError, but so does a client fault (`null.getReader()`), and that one
// must surface on the first hop rather than be retried until the deadline. The same
// vocabulary as `isAbortLikeResumeError` (forecastClient.js), minus the abort words.
const TRANSPORT_FAILURE_MESSAGE = /failed to fetch|fetch failed|network ?error|load failed/i;

/**
 * Did a request die WITHOUT the server saying anything? A network failure — the socket
 * dropped, the request reset by Cloud Run at `timeoutSeconds`, a proxy recycled — never
 * arrives as an HTTP status (#5641), and says nothing about the forecast: the row may well
 * still be `running`, so the recovery loop treats it like a clean EOF and asks `/result`.
 * An abort is a decision, an `HTTP <status>` is an answer, and any other error is a fault;
 * none of those is a transport failure.
 */
export function isTransportError(error) {
  if (!error || error.name === "AbortError") return false;
  return TRANSPORT_FAILURE_MESSAGE.test(error.message || "");
}

// A result lookup is a point read, not another long-lived stream. Race even
// injected readers: the shared API client intentionally never resolves after
// a login redirect, and aborting fetch alone cannot settle that wrapper.
/** Bound a result point read, including API wrappers that ignore cancellation. */
export async function boundedResultInspection(traceId, signal, getResult, opts = {}) {
  if (signal?.aborted) throw cancelledError();
  const now = opts.now || Date.now;
  const parsedDeadline =
    typeof opts.recoveryDeadlineAt === "number"
      ? opts.recoveryDeadlineAt
      : Date.parse(opts.recoveryDeadlineAt || "");
  const recoveryDeadlineAt = Number.isFinite(parsedDeadline) ? parsedDeadline : Infinity;
  const controller = new AbortController();
  let timer;
  let onAbort;
  const interruption = new Promise((_resolve, reject) => {
    onAbort = () => {
      reject(cancelledError());
      controller.abort(signal?.reason);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    const timeoutMs = opts.resultInspectionTimeoutMs ?? 10_000;
    const remaining = recoveryDeadlineAt - now();
    timer = setTimeout(
      () => {
        reject(
          now() >= recoveryDeadlineAt
            ? recoveryWaitExpiredError()
            : resultInspectionTimedOutError(),
        );
        controller.abort();
      },
      remaining > 0 ? Math.min(timeoutMs, remaining) : timeoutMs,
    );
  });
  try {
    return await Promise.race([getResult(traceId, { signal: controller.signal }), interruption]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

export function isRecoveryWaitExpiredError(error) {
  return error?.code === RECOVERY_WAIT_EXPIRED;
}

/** Did a result point read stall, rather than answer? */
export function isResultInspectionTimeoutError(error) {
  return error?.code === RESULT_INSPECTION_TIMED_OUT;
}

/**
 * Did recovery end on silence rather than on an answer — a result read that stalled, or a
 * request that died without a status? Either way the forecast may well still be running,
 * so the caller must leave it resumable instead of declaring it failed.
 */
export function isUnansweredRecoveryError(error) {
  return isResultInspectionTimeoutError(error) || isTransportError(error);
}

/**
 * `boundedResultInspection`, with a stalled read retried rather than fatal.
 *
 * A stall says nothing about whether the result exists, so letting one end the recovery
 * discards a result that is already durable. Bounded by attempts and by the recovery
 * deadline. An abort and an expired budget are decisions, not stalls, and pass through.
 *
 * Both post-stream reads go through this — the terminal-`done` read and the after-EOF
 * read — so neither can regain the behavior independently.
 */
export async function retryingResultInspection(traceId, signal, getResult, opts = {}) {
  const now = opts.now || Date.now;
  const sleep = opts.sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const retryDelayMs = opts.retryDelayMs ?? 2000;
  const attempts = opts.resultInspectionAttempts ?? 3;
  const deadlineAt =
    typeof opts.recoveryDeadlineAt === "number" ? opts.recoveryDeadlineAt : Infinity;
  for (let attempt = 1; ; attempt++) {
    try {
      return await boundedResultInspection(traceId, signal, getResult, opts);
    } catch (error) {
      if (!isResultInspectionTimeoutError(error)) throw error;
      if (attempt >= attempts) throw error;
      await sleep(Math.min(retryDelayMs, Math.max(0, deadlineAt - now())));
      // Re-checked AFTER the backoff, not before: a sleep that lands ON the deadline must
      // not start another read. `boundedResultInspection` falls back to the full timeout
      // when no budget remains, so that read would delay the expiry by the whole bound.
      if (now() >= deadlineAt) throw recoveryWaitExpiredError();
    }
  }
}

export function resolveRecoveryDeadlineAt(startedAt, exactDeadlineAt, now = Date.now) {
  const exact =
    typeof exactDeadlineAt === "number" ? exactDeadlineAt : Date.parse(exactDeadlineAt || "");
  const started = typeof startedAt === "number" ? startedAt : Date.parse(startedAt || "");
  if (Number.isFinite(started)) {
    const safetyFloor = started + FORECAST_RECOVERY_WINDOW_MS;
    return Number.isFinite(exact) ? Math.min(exact, safetyFloor) : safetyFloor;
  }
  if (Number.isFinite(exact)) return exact;
  // Missing/corrupt original-start metadata must not mint a new 262-minute wait
  // on every reload. Expire immediately while leaving the durable handle intact.
  return now();
}

/**
 * Re-open a resume stream after it ends without a terminal — a clean EOF or a transport
 * failure (`TypeError: network error`, #5641) — only when the persisted result still says
 * 404 (running). Cloud Run kills each hop at `timeoutSeconds`, and which of the two the
 * browser sees is not the client's to control; neither says anything about the run. One
 * absolute deadline governs every hop and is never renewed by a reconnect. Terminal SSE,
 * an HTTP refusal (auth, 404/410 — an answer), cancellation, and a recovered result all
 * stop the loop; a transport failure or a stall (`result_inspection_timed_out`) of the
 * `/result` probe itself is likewise not an answer and is re-asked on the next hop.
 */
export async function* resumeForecastStreamThroughEof(traceId, signal, opts = {}) {
  const resumeStream = opts.resumeStream || resumeForecastStream;
  const fetchImpl = opts.fetchImpl || fetch;
  const getResult =
    opts.getResult ||
    (async (_traceId, { signal: probeSignal }) => {
      const response = await fetchImpl(`/api/forecasts/${encodeURIComponent(traceId)}/result`, {
        credentials: "include",
        signal: probeSignal,
      });
      return {
        ok: response.ok,
        status: response.status,
        result: response.ok ? await response.json() : undefined,
      };
    });
  const now = opts.now || Date.now;
  const sleep = opts.sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const retryDelayMs = opts.retryDelayMs ?? 2000;
  const recoveryDeadlineAt = resolveRecoveryDeadlineAt(
    opts.startedAt,
    opts.recoveryDeadlineAt,
    now,
  );
  // The typed-update cursor across reconnects: seeded by the caller (what the
  // initial stream or the store already folded), advanced by every positioned
  // `update` relayed here, and sent on each attempt so the server replays exactly
  // the gap — no missed frames, no double-fold.
  let sinceSeq = typedSeqOf(opts.sinceSeq);

  // `{ recovered: false }` covers a 404 (the run is live) and two non-answers: a transport
  // failure of the probe and a probe that stalled through every bounded attempt. None says
  // the run ended, so all back off and reconnect; the absolute deadline still bounds this.
  const inspectResult = async () => {
    let result;
    try {
      result = await retryingResultInspection(traceId, signal, getResult, {
        now,
        sleep,
        retryDelayMs,
        recoveryDeadlineAt,
        resultInspectionTimeoutMs: opts.resultInspectionTimeoutMs,
        resultInspectionAttempts: opts.resultInspectionAttempts,
      });
    } catch (error) {
      if (signal?.aborted) throw error;
      if (!isTransportError(error) && !isResultInspectionTimeoutError(error)) throw error;
      return { recovered: false };
    }
    if (result.ok) return { recovered: true, result: result.result };
    // The shared API client never throws: a network failure comes back as status 0.
    if (result.status === 0) return { recovered: false };
    if (result.status !== 404) throw new Error(`Result fetch returned ${result.status}`);
    return { recovered: false };
  };

  while (true) {
    if (signal?.aborted) throw cancelledError();
    // Never mint a new long-lived resume request after the absolute wait budget.
    // One final point read can still recover a result that became durable at the
    // boundary; a 404 means the run remains live but this tab stops waiting.
    if (now() >= recoveryDeadlineAt) {
      const finalResult = await inspectResult();
      if (finalResult.recovered) {
        yield { type: "recoveredResult", data: finalResult.result };
        return;
      }
      throw recoveryWaitExpiredError();
    }

    // A resume GET can itself sit open until the proxy's long timeout. Give each
    // attempt a linked signal whose deadline abort is local to recovery: caller
    // cancellation remains AbortError, while the absolute deadline becomes the
    // safe transient classifier after one final result inspection.
    const attemptController = new AbortController();
    let deadlineExpired = false;
    const onCallerAbort = () => attemptController.abort(signal?.reason);
    signal?.addEventListener("abort", onCallerAbort, { once: true });
    const deadlineTimer = setTimeout(
      () => {
        deadlineExpired = true;
        attemptController.abort();
      },
      Math.max(0, recoveryDeadlineAt - now()),
    );
    let terminal = false;
    try {
      for await (const event of resumeStream(traceId, attemptController.signal, {
        fetchImpl,
        sinceSeq,
        ...(opts.resumeOptions || {}),
      })) {
        const emitted =
          event.type === FRAME.DONE &&
          (typeof event.data?.result_url !== "string" || event.data.result_url.length === 0)
            ? { type: "invalidDone", data: event.data }
            : event;
        yield emitted;
        // The cursor acknowledges a frame only once the consumer has taken it (control
        // returns here after the consumer's loop body ran), never before the handoff.
        // A consumer that swallowed its fold's failure marks the yielded event
        // `delivered: false` so the frame stays replayable after the next cut.
        if (event.type === "update" && event.delivered !== false) {
          const seq = typedSeqOf(event.data?.seq);
          if (seq !== null && (sinceSeq === null || seq > sinceSeq)) sinceSeq = seq;
        }
        if (
          event.type === FRAME.DONE ||
          event.type === FRAME.ERROR ||
          event.type === FRAME.CANCELLED
        ) {
          terminal = true;
          break;
        }
      }
    } catch (error) {
      if (signal?.aborted) throw cancelledError();
      if (deadlineExpired || now() >= recoveryDeadlineAt) {
        deadlineExpired = true;
      } else if (!isTransportError(error)) {
        throw error; // an HTTP refusal or a real fault: an answer, so the loop ends
      }
      // A transport failure ends this hop exactly as a clean EOF would (#5641): fall
      // through to the result inspection, and reconnect from the carried cursor on 404.
    } finally {
      clearTimeout(deadlineTimer);
      signal?.removeEventListener("abort", onCallerAbort);
    }
    if (terminal) return;
    if (deadlineExpired || now() >= recoveryDeadlineAt) continue;

    const result = await inspectResult();
    if (result.recovered) {
      yield { type: "recoveredResult", data: result.result };
      return;
    }
    await sleep(Math.min(retryDelayMs, Math.max(0, recoveryDeadlineAt - now())));
  }
}

// Mid-stream recovery for a forecast that was already trace-allocated.
//
// Why this exists: Safari occasionally drops a long-lived fetch reader
// near the end of the stream, where the forecaster has already written
// the result to GCS but the SSE `done` frame hasn't reached the browser.
// The first thing to try is the cheap one — poll /result. If it's there,
// we hand it back to the caller and the user sees the rendered forecast
// instead of a "Load failed" toast. If it's still running (404 from
// chatboard, since `getForecastResult` proxies through the forecaster
// which returns 404 until persistence completes), fall back to the
// resume SSE stream and ride it through to `done`.
//
// Returns the parsed result JSON. Throws on any non-recoverable failure
// (auth error, abort, resume stream error). The caller is responsible
// for clearing the pendingForecast sessionStorage handle on success.
//
// `onProgress({stage, step, progress, ...})` is fired for catchup + progress
// events and `onUpdate(update)` for typed progress events during the resume
// fallback so neither UI channel goes silent after a transport interruption.
// `opts.sinceSeq` is the highest typed `update` seq the interrupted stream
// delivered; the resume replays from strictly after it (#5611).
export async function tryRecoverMidStream(traceId, signal, opts = {}) {
  const fetchImpl = opts.fetchImpl || fetch;
  const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;
  const onUpdate = typeof opts.onUpdate === "function" ? opts.onUpdate : null;
  const onNarration = typeof opts.onNarration === "function" ? opts.onNarration : null;
  const pollIntervalMs = opts.pollIntervalMs ?? 2000;
  const pollDeadlineMs = opts.pollDeadlineMs ?? 30000;
  const sleep = opts.sleep || ((ms) => new Promise((res) => setTimeout(res, ms)));

  const resultUrl = `/api/forecasts/${encodeURIComponent(traceId)}/result`;
  const now = opts.now || Date.now;
  const recoveryDeadlineAt = resolveRecoveryDeadlineAt(
    opts.startedAt,
    opts.recoveryDeadlineAt,
    now,
  );
  const deadline = Math.min(now() + pollDeadlineMs, recoveryDeadlineAt);
  const readResult = async (_traceId, { signal: probeSignal }) => {
    const response = await fetchImpl(resultUrl, { credentials: "include", signal: probeSignal });
    return {
      ok: response.ok,
      status: response.status,
      result: response.status === 200 ? await response.json() : undefined,
    };
  };
  while (now() < deadline) {
    if (signal && signal.aborted) {
      throw Object.assign(new Error("cancelled"), { name: "AbortError" });
    }
    try {
      const r = await boundedResultInspection(traceId, signal, readResult, {
        recoveryDeadlineAt,
        now,
        resultInspectionTimeoutMs: Math.min(
          opts.resultInspectionTimeoutMs ?? 10_000,
          Math.max(1, deadline - now()),
        ),
      });
      if (r.status === 200) return r.result;
      if (r.status === 404) {
        break;
      }
      // Other statuses (502/503): keep polling — transient upstream.
    } catch (e) {
      if (e && (e.name === "AbortError" || isRecoveryWaitExpiredError(e))) throw e;
      // Network blip — keep polling until deadline.
    }
    await sleep(Math.min(pollIntervalMs, Math.max(0, deadline - now())));
  }

  // Forecast still running (or polling timed out): switch to the resume
  // SSE stream. resumeForecastStream yields { type, data }; this iterates
  // through to `done`, then we fetch /result for the final payload.
  let sawDone = false;
  for await (const evt of resumeForecastStreamThroughEof(traceId, signal, {
    fetchImpl,
    sinceSeq: opts.sinceSeq,
    recoveryDeadlineAt,
    startedAt: opts.startedAt,
    now: opts.now,
    sleep: opts.recoverySleep || sleep,
    retryDelayMs: opts.resumeRetryDelayMs,
    resultInspectionTimeoutMs: opts.resultInspectionTimeoutMs,
  })) {
    if (evt.type === "catchup") {
      if (onProgress) {
        const d = evt.data || {};
        try {
          onProgress({ stage: d.last_stage, step: d.last_step, progress: d.last_progress });
        } catch (_cbErr) {
          /* ignore */
        }
      }
    } else if (evt.type === "progress") {
      if (onProgress) {
        try {
          onProgress(evt.data || {});
        } catch (_cbErr) {
          /* ignore */
        }
      }
    } else if (evt.type === "update") {
      if (onUpdate) {
        try {
          onUpdate(evt.data || {});
        } catch (_cbErr) {
          // A UI hiccup must not drop the forecast — but the frame was not folded,
          // so the reconnect cursor must not acknowledge it (see the helper).
          evt.delivered = false;
        }
      }
    } else if (evt.type === "narration") {
      if (onNarration) {
        try {
          onNarration(evt.data || {});
        } catch (_cbErr) {
          // Narration is decoration. Unlike `update` this does NOT touch
          // `delivered`: the reconnect cursor tracks typed seqs only, and a
          // dropped beat is a missing chat line, not a hole in the forecast.
        }
      }
    } else if (evt.type === "done") {
      if (typeof evt.data?.result_url !== "string" || evt.data.result_url.length === 0) {
        throw Object.assign(new Error("Resume stream emitted invalid done event"), {
          code: "invalid_resume_done",
          terminal: true,
        });
      }
      sawDone = true;
    } else if (evt.type === "recoveredResult") {
      return evt.data;
    } else if (evt.type === "invalidDone") {
      throw Object.assign(new Error("Resume stream emitted invalid done event"), {
        code: INVALID_RESUME_DONE,
        terminal: true,
      });
    } else if (evt.type === "error") {
      const code = (evt.data && (evt.data.code || evt.data.message)) || "forecast error";
      throw Object.assign(new Error(code), evt.data?.code ? { code: evt.data.code } : {});
    } else if (evt.type === "cancelled") {
      throw Object.assign(new Error("cancelled"), { name: "AbortError" });
    }
  }
  if (!sawDone) {
    throw new Error("resume ended without done event");
  }
  const rr = await retryingResultInspection(traceId, signal, readResult, {
    ...opts,
    recoveryDeadlineAt,
    now,
  });
  if (!rr.ok) {
    const error = new Error(`Result fetch returned ${rr.status}`);
    if (rr.status === 404) error.code = "forecast_result_pending";
    throw error;
  }
  return rr.result;
}
